import { Controller } from "@hotwired/stimulus"
import { hideIntercom, showIntercom } from "../helpers/intercom"
import { canSignWithoutConsultant, setSkipConsultantMode, withSkipConsultant } from "../lib/sign_without_consultant"

const SVG_CHECK = '<svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>'
const SVG_INFO_CIRCLE = '<svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"/></svg>'
const SVG_LINK = '<svg class="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"/></svg>'

export default class extends Controller {
  static targets = [
    "newPcoModal", "modalTitle", "pcoTitle",
    "pcoLineItems", "pcoModalTotal", "pcoModalDaysTotal",
    "pcoModalContractTotal", "pcoModalPrevTotal", "pcoModalCoTotalsTotal", "pcoModalBalanceTotal",
    "deleteModal", "voidModal", "unvoidModal", "signModal", "unsignModal", "withdrawAllModal", "requestApprovalModal",
    "modalSkipConsultantBtn", "modalConsultantSkipped", "skipConsultantNote", "signTitle", "signConfirmBtn",
    "pcoDropzone", "pcoFileInput", "pcoFileList",
    "addPastPcoBtn", "saveDraftBtn", "signRequestBtn",
    "pastPcoConfirmModal", "pastPcoSequenceLabel", "pastPcoNegativeWarning",
    "pastPcoTitlePrefix", "pastPcoConfirmBody",
    "pcoHistoryLog", "pcoHistoryLogEntries",
    "modalRequestApprovalBtn", "modalSignedBadge", "modalSignedText", "modalSignedTooltip", "modalSignedCheck",
    "modalPendingText",
    "modalLinkedCo", "modalLinkedCoText",
    "unsignBtn", "withdrawAllBtn", "modalDeleteBtn", "modalVoidBtn", "modalUnvoidBtn", "modalEditPastBtn", "modalApproveBtn", "modalRejectBtn",
    "rejectPcoModal", "rejectComment",
    "overallTotals", "overallAmount", "overallDays", "pcoCards", "emptyState",
    "uploadSection", "uploadLabel",
    "issueCcdBtn", "updateCcdBtn", "issueCcdModal", "issueCcdNotifyList", "deleteCcdModal"
  ]

  static values = {
    projectId: Number,
    currency: String,
    currencyFormat: String,
    delimiter: String,
    separator: String,
    isOwner: Boolean,
    // PM Permissions: stakeholder granted owner-side PCO approval
    // (approve/reject side only — no editing, voiding or deleting).
    canApprove: Boolean,
    // Approval Settings → "Allow Owners To Sign Without The Consultant".
    skipConsultantAllowed: Boolean,
    isConsultant: Boolean,
    isContractor: Boolean,
    sovApproved: Boolean,
    hasConsultant: Boolean,
    ccdEnabled: Boolean
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._editSequence = null
    this._isPast = false
    this._pendingDeleteSequence = null
    this._pendingSignSequence = null
    this._lineItemsData = []
    this._attachmentIds = []
    this._pendingUploads = 0
    this._previousTotals = {}
    this._signFromEditModal = false
    this._signFromEditIsNew = false
    this._signFromEditSaveResp = null
    this._approveAfterAcknowledge = false
    this._linkedCo = null
    this._isVoid = false
    this._requiredSigs = this.hasConsultantValue ? 3 : 2
    this._pdfPollingTimers = {}
    this._mode = "pco" // "pco" or "ccd"
    this._ccdNotifyUsers = []
    this._ccdData = null
  }

  disconnect() {
    Object.values(this._pdfPollingTimers).forEach(t => clearTimeout(t))
    this._pdfPollingTimers = {}
  }

  // -- Currency helpers --

  _parseCurrency(str) {
    if (!str) return 0
    let cleaned = str.replace(this.currencyValue, "").trim()
    let neg = false
    if (cleaned.startsWith("(") && cleaned.endsWith(")")) {
      neg = true
      cleaned = cleaned.slice(1, -1)
    }
    if (this.currencyFormatValue === "european_currency_format") {
      cleaned = cleaned.replace(/\./g, "").replace(",", ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      cleaned = cleaned.replace(/'/g, "")
    } else {
      cleaned = cleaned.replace(/,/g, "")
    }
    const val = parseFloat(cleaned) || 0
    return neg ? -val : val
  }

  _formatCurrency(num) {
    if (num === null || num === undefined || isNaN(num)) return ""
    const abs = Math.abs(num)
    let formatted
    if (this.currencyFormatValue === "european_currency_format") {
      formatted = abs.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      formatted = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'")
    } else {
      formatted = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    }
    if (num < 0) return `${this.currencyValue}(${formatted})`
    return `${this.currencyValue}${formatted}`
  }

  _formatAmount(num) {
    if (num === null || num === undefined || isNaN(num)) return ""
    const abs = Math.abs(num)
    let formatted
    if (this.currencyFormatValue === "european_currency_format") {
      formatted = abs.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      formatted = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'")
    } else {
      formatted = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    }
    if (num < 0) return `(${formatted})`
    return formatted
  }

  // -- New PCO --

  async newPco() {
    if (!this.sovApprovedValue) {
      this._showToast("Schedule of values must be approved before creating a PCO.", "alert")
      return
    }
    this._editSequence = null
    this._isPast = false
    this._linkedCo = null
    this._isVoid = false
    this.modalTitleTarget.textContent = "New PCO"
    await this._loadLineItemsAndShowModal()
  }

  async newPastPco() {
    if (!this.sovApprovedValue) {
      this._showToast("Schedule of values must be approved before creating a PCO.", "alert")
      return
    }
    this._editSequence = null
    this._isPast = true
    this._linkedCo = null
    this._isVoid = false
    this.modalTitleTarget.textContent = "Past PCO"
    await this._loadLineItemsAndShowModal()
  }

  async editPco(event) {
    const sequence = event.currentTarget.dataset.sequence
    this._editSequence = sequence
    const card = this.element.querySelector(`.pco-card[data-sequence="${sequence}"]`)
    this._isPast = card?.dataset.isPast === "true"
    this._isVoid = card?.dataset.isVoid === "true"
    this.modalTitleTarget.textContent = this._isVoid ? `PCO #${sequence}` : this._isPast ? `Past PCO #${sequence}` : `Edit PCO #${sequence}`
    await this._loadLineItemsAndShowModal(sequence)
  }

  async _loadLineItemsAndShowModal(sequence = null) {
    const isCcd = this._mode === "ccd"
    const endpoint = isCcd ? "new_ccd_modal_data" : "new_proposed_change_order_modal_data"
    const url = `/projects/${this.projectIdValue}/finances/${endpoint}${sequence ? `?sequence=${sequence}` : ""}`
    const resp = await fetch(url, { headers: { "Accept": "application/json" } })
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      this._showToast(err.error || "An error occurred.", "alert")
      return
    }
    const data = await resp.json()

    this._lineItemsData = data.line_items
    this._previousTotals = data.previous_totals || {}
    this._attachmentIds = []
    this._pendingUploads = 0
    this._linkedCo = data.linked_co || null

    if (isCcd) {
      this._ccdData = data.ccd_data || null
      this._ccdNotifyUsers = data.notify_users || []
      if (this.hasPcoTitleTarget) this.pcoTitleTarget.value = data.ccd_data?.title || ""
    } else {
      this._requiredSigs = data.required_sigs || (this.hasConsultantValue ? 3 : 2)
      if (this.hasPcoTitleTarget) this.pcoTitleTarget.value = data.pco_data?.title || ""
    }

    if (this.hasPcoFileListTarget) this.pcoFileListTarget.innerHTML = ""

    const existingItems = isCcd ? (data.ccd_data?.items || []) : (data.pco_data?.items || [])
    this._renderPcoLineItems(data.line_items, existingItems, this._previousTotals, data.balances || {}, data.contractor_fee || 0, data.previous_fee_total || 0, data.fee_balance || 0)

    // Load existing attachments when editing
    if (data.attachments && data.attachments.length > 0) {
      data.attachments.forEach(att => {
        this._attachmentIds.push(att.id)
        const chip = this._createFileChip(att.filename, {
          removable: true,
          url: att.url,
          onRemove: () => {
            chip.remove()
            this._attachmentIds = this._attachmentIds.filter(id => id !== att.id)
          }
        })
        if (this.hasPcoFileListTarget) this.pcoFileListTarget.appendChild(chip)
      })
    }

    this._renderHistoryLog(data.history_log || [])

    if (isCcd) {
      this._renderCcdModalHeaderBadges(data)
    } else {
      this._renderModalHeaderBadges(data)
    }

    // Toggle PCO-only vs CCD-only buttons (only when in CCD mode;
    // for PCO mode _renderModalHeaderBadges already sets correct visibility)
    if (isCcd) {
      // Hide PCO-only buttons; Issue/Update CCD visibility is set by _renderCcdModalHeaderBadges
      if (this.hasAddPastPcoBtnTarget) this.addPastPcoBtnTarget.classList.add("hidden")
      if (this.hasSignRequestBtnTarget) this.signRequestBtnTarget.classList.add("hidden")
      if (this.hasModalApproveBtnTarget) this.modalApproveBtnTarget.classList.add("hidden")
      if (this.hasModalRejectBtnTarget) this.modalRejectBtnTarget.classList.add("hidden")
      if (this.hasUnsignBtnTarget) this.unsignBtnTarget.classList.add("hidden")
      if (this.hasWithdrawAllBtnTarget) this.withdrawAllBtnTarget.classList.add("hidden")
      if (this.hasModalRequestApprovalBtnTarget) this.modalRequestApprovalBtnTarget.classList.add("hidden")
    } else {
      if (this.hasAddPastPcoBtnTarget) this.addPastPcoBtnTarget.classList.toggle("hidden", !!this._editSequence)
      if (this.hasIssueCcdBtnTarget) this.issueCcdBtnTarget.classList.add("hidden")
      if (this.hasUpdateCcdBtnTarget) this.updateCcdBtnTarget.classList.add("hidden")
    }

    // For CCD: set readonly if editing a non-editable CCD or linked to a CO
    if (isCcd) {
      const ccdLocked = this._editSequence && (this._linkedCo || (this._ccdData && !this._ccdData.editable))
      this._setModalReadonly(!!ccdLocked)
    }

    this.newPcoModalTarget.classList.remove("hidden")
    hideIntercom()
  }

  _renderPcoLineItems(lineItems, existingItems, previousTotals, balances, contractorFee = 0, previousFeeTotal = 0, feeBalance = 0) {
    const existing = {}
    existingItems.forEach(item => {
      if (item.is_fee) {
        existing["fee"] = item
      } else {
        existing[item.line_item_id] = item
      }
    })
    const cur = this.currencyValue

    let html = ""
    lineItems.forEach(li => {
      const ex = existing[li.id]
      const contractAmount = parseFloat(li.contract_amount) || 0
      const prevTotal = parseFloat(previousTotals[li.id]) || 0
      const currentAmount = ex ? (parseFloat(ex.amount) || 0) : 0
      const coTotal = contractAmount + prevTotal + currentAmount
      const balance = parseFloat(balances[li.id]) || 0

      html += `
        <tr data-line-item-id="${li.id}">
          <td class="px-2 py-1.5 border-r border-gray-200 text-[12px] truncate">${li.item || ""}</td>
          <td class="px-2 py-1.5 border-r border-gray-200 text-[12px] truncate">${li.csi_div || ""}</td>
          <td class="px-2 py-1.5 text-[12px] truncate">${li.description || ""}</td>
          <td class="px-0 py-1.5 border-r border-gray-200 text-center">
            ${li.definition ? `<span class="cursor-help text-gray-400" data-tip="${this._escAttr(li.definition)}" data-tip-align="right">${SVG_INFO_CIRCLE}</span>` : ""}
          </td>
          <td class="px-0 py-0 border-r border-gray-200 bg-amber-50">
            <input type="text" value="${this._escAttr(ex?.comment || "")}" data-field="comment"
              class="w-full px-2 py-1.5 text-[12px] bg-transparent focus:outline-none focus:ring-1 focus:ring-brand">
          </td>
          <td class="px-0 py-0 border-r border-gray-200">
            <div class="flex items-center">
              <span class="pl-2 text-gray-400 select-none text-[12px]" data-sym="contract">${contractAmount ? cur : ""}</span>
              <span class="w-full px-1 py-1.5 pr-2 text-right text-[12px]" data-field="contract" data-contract-amount="${contractAmount}">${contractAmount ? this._formatAmount(contractAmount) : ""}</span>
            </div>
          </td>
          <td class="px-0 py-0 border-r border-gray-200">
            <div class="flex items-center">
              <span class="pl-2 text-gray-400 select-none text-[12px]" data-sym="prev-total">${prevTotal ? cur : ""}</span>
              <span class="w-full px-1 py-1.5 pr-2 text-right text-[12px]" data-field="prev-total" data-prev-total="${prevTotal}">${prevTotal ? this._formatAmount(prevTotal) : ""}</span>
            </div>
          </td>
          <td class="px-0 py-0 border-r border-gray-200 bg-amber-50">
            <div class="flex items-center">
              <span class="pl-2 text-gray-400 select-none text-[12px]">${cur}</span>
              <input type="text" value="${ex ? this._formatAmount(currentAmount) : ""}" data-field="amount"
                data-action="input->proposed-change-order#recalcRowTotal blur->proposed-change-order#formatAmountOnBlur"
                class="w-full px-1 py-1.5 pr-2 text-right text-[12px] bg-transparent focus:outline-none">
            </div>
          </td>
          <td class="px-0 py-0 border-r border-gray-200">
            <div class="flex items-center">
              <span class="pl-2 text-gray-400 select-none text-[12px]" data-sym="co-total">${coTotal ? cur : ""}</span>
              <span class="w-full px-1 py-1.5 pr-2 text-right text-[12px]" data-field="co-total">${coTotal ? this._formatAmount(coTotal) : ""}</span>
            </div>
          </td>
          <td class="px-0 py-0 border-r border-gray-200${Math.round((balance + currentAmount) * 100) / 100 < 0 ? " bg-red-50" : ""}">
            <div class="flex items-center">
              <span class="pl-2 select-none text-[12px]${Math.round((balance + currentAmount) * 100) / 100 < 0 ? " text-red-600" : " text-gray-400"}" data-sym="balance">${(balance + currentAmount) ? cur : ""}</span>
              <span class="w-full px-1 py-1.5 pr-2 text-right text-[12px]${Math.round((balance + currentAmount) * 100) / 100 < 0 ? " text-red-600 font-semibold" : ""}" data-field="balance" data-base-balance="${balance}">${(balance + currentAmount) ? this._formatAmount(balance + currentAmount) : ""}</span>
            </div>
          </td>
          <td class="px-0 py-0 bg-amber-50">
            <input type="text" value="${this._escAttr(ex?.days || "")}" data-field="days"
              data-action="input->proposed-change-order#recalcDays"
              class="w-full px-2 py-1.5 text-[12px] text-right bg-transparent focus:outline-none">
          </td>
        </tr>`
    })

    // Contractor's Fee row — the fee is part of the contract sum (GMP), so it
    // participates in every column and the footer Total like a regular line item.
    const feeEx = existing["fee"]
    const feeBaseline = parseFloat(contractorFee) || 0
    const feePrevTotal = parseFloat(previousFeeTotal) || 0
    const feeAmount = feeEx ? (parseFloat(feeEx.amount) || 0) : 0
    const feeCoTotal = feeBaseline + feePrevTotal + feeAmount
    const feeBase = parseFloat(feeBalance) || 0
    const feeBalanceVal = Math.round((feeBase + feeAmount) * 100) / 100
    const feeNeg = feeBalanceVal < 0
    const feeLabel = lineItems.length > 0 ? (lineItems.length + 1) : 1
    html += `
      <tr data-is-fee="true">
        <td class="px-2 py-1.5 border-r border-gray-200 text-[12px]">${feeLabel}</td>
        <td class="px-2 py-1.5 border-r border-gray-200 text-[12px]"></td>
        <td class="px-2 py-1.5 text-[12px] font-medium" colspan="1">Contractor's Fee</td>
        <td class="px-0 py-1.5 border-r border-gray-200"></td>
        <td class="px-0 py-0 border-r border-gray-200 bg-amber-50">
          <input type="text" value="${this._escAttr(feeEx?.comment || "")}" data-field="comment"
            class="w-full px-2 py-1.5 text-[12px] bg-transparent focus:outline-none focus:ring-1 focus:ring-brand">
        </td>
        <td class="px-0 py-0 border-r border-gray-200">
          <div class="flex items-center">
            <span class="pl-2 text-gray-400 select-none text-[12px]" data-sym="contract">${feeBaseline ? cur : ""}</span>
            <span class="w-full px-1 py-1.5 pr-2 text-right text-[12px]" data-field="contract" data-contract-amount="${feeBaseline}">${feeBaseline ? this._formatAmount(feeBaseline) : ""}</span>
          </div>
        </td>
        <td class="px-0 py-0 border-r border-gray-200">
          <div class="flex items-center">
            <span class="pl-2 text-gray-400 select-none text-[12px]" data-sym="prev-total">${feePrevTotal ? cur : ""}</span>
            <span class="w-full px-1 py-1.5 pr-2 text-right text-[12px]" data-field="prev-total" data-prev-total="${feePrevTotal}">${feePrevTotal ? this._formatAmount(feePrevTotal) : ""}</span>
          </div>
        </td>
        <td class="px-0 py-0 border-r border-gray-200 bg-amber-50">
          <div class="flex items-center">
            <span class="pl-2 text-gray-400 select-none text-[12px]">${cur}</span>
            <input type="text" value="${feeEx ? this._formatAmount(feeAmount) : ""}" data-field="amount"
              data-action="input->proposed-change-order#recalcRowTotal blur->proposed-change-order#formatAmountOnBlur"
              class="w-full px-1 py-1.5 pr-2 text-right text-[12px] bg-transparent focus:outline-none">
          </div>
        </td>
        <td class="px-0 py-0 border-r border-gray-200">
          <div class="flex items-center">
            <span class="pl-2 text-gray-400 select-none text-[12px]" data-sym="co-total">${feeCoTotal ? cur : ""}</span>
            <span class="w-full px-1 py-1.5 pr-2 text-right text-[12px]" data-field="co-total">${feeCoTotal ? this._formatAmount(feeCoTotal) : ""}</span>
          </div>
        </td>
        <td class="px-0 py-0 border-r border-gray-200${feeNeg ? " bg-red-50" : ""}">
          <div class="flex items-center">
            <span class="pl-2 select-none text-[12px]${feeNeg ? " text-red-600" : " text-gray-400"}" data-sym="balance">${feeBalanceVal ? cur : ""}</span>
            <span class="w-full px-1 py-1.5 pr-2 text-right text-[12px]${feeNeg ? " text-red-600 font-semibold" : ""}" data-field="balance" data-base-balance="${feeBase}">${feeBalanceVal ? this._formatAmount(feeBalanceVal) : ""}</span>
          </div>
        </td>
        <td class="px-0 py-0 bg-amber-50">
          <input type="text" value="${this._escAttr(feeEx?.days || "")}" data-field="days"
            data-action="input->proposed-change-order#recalcDays"
            class="w-full px-2 py-1.5 text-[12px] text-right bg-transparent focus:outline-none">
        </td>
      </tr>`

    this.pcoLineItemsTarget.innerHTML = html
    this._recalcFooterTotals()
  }

  _escAttr(str) {
    return String(str).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }

  // -- Calculation --

  recalcRowTotal(event) {
    const row = event.target.closest("tr")
    this._updateRowTotal(row)
    this._recalcFooterTotals()
  }

  formatAmountOnBlur(event) {
    const input = event.target
    const val = this._parseCurrency(input.value)
    input.value = val !== 0 ? this._formatAmount(val) : ""
  }

  recalcDays() {
    this._recalcFooterTotals()
  }

  _updateRowTotal(row) {
    // The Contractor's Fee row carries the same data attributes as regular line items
    // (it's part of the contract sum / GMP), so it recalculates here uniformly.
    const contractAmount = parseFloat(row.querySelector("[data-contract-amount]")?.dataset.contractAmount) || 0
    const prevTotal = parseFloat(row.querySelector("[data-prev-total]")?.dataset.prevTotal) || 0
    const amount = this._parseCurrency(row.querySelector("[data-field='amount']")?.value)
    const coTotal = contractAmount + prevTotal + amount
    const coTotalSpan = row.querySelector("[data-field='co-total']")
    if (coTotalSpan) {
      coTotalSpan.textContent = coTotal ? this._formatAmount(coTotal) : ""
      const coSym = row.querySelector("[data-sym='co-total']")
      if (coSym) coSym.textContent = coTotal ? this.currencyValue : ""
    }
    const baseBalance = parseFloat(row.querySelector("[data-base-balance]")?.dataset.baseBalance) || 0
    const balVal = Math.round((baseBalance + amount) * 100) / 100
    const balanceSpan = row.querySelector("[data-field='balance']")
    if (balanceSpan) {
      balanceSpan.textContent = balVal ? this._formatAmount(balVal) : ""
      const balSym = row.querySelector("[data-sym='balance']")
      if (balSym) balSym.textContent = balVal ? this.currencyValue : ""
      const balanceCell = balanceSpan.closest("td")
      if (balanceCell) {
        if (balVal < 0) {
          balanceCell.classList.add("bg-red-50")
          balanceSpan.classList.add("text-red-600", "font-semibold")
          if (balSym) balSym.classList.add("text-red-600")
        } else {
          balanceCell.classList.remove("bg-red-50")
          balanceSpan.classList.remove("text-red-600", "font-semibold")
          if (balSym) balSym.classList.remove("text-red-600")
        }
      }
    }
  }

  _recalcFooterTotals() {
    let contractTotal = 0, prevTotal = 0, amountTotal = 0, coTotalsTotal = 0, balanceTotal = 0, daysTotal = 0

    this.pcoLineItemsTarget.querySelectorAll("tr").forEach(row => {
      // The Contractor's Fee row is part of the contract sum (GMP), so it folds into
      // every column total just like a regular line item — the footer Total foots to
      // the full contract amount including the fee.
      contractTotal += parseFloat(row.querySelector("[data-contract-amount]")?.dataset.contractAmount) || 0
      prevTotal += parseFloat(row.querySelector("[data-prev-total]")?.dataset.prevTotal) || 0
      coTotalsTotal += this._parseCurrency(row.querySelector("[data-field='co-total']")?.textContent)
      const baseBalance = parseFloat(row.querySelector("[data-base-balance]")?.dataset.baseBalance) || 0
      const amt = this._parseCurrency(row.querySelector("[data-field='amount']")?.value)
      balanceTotal += baseBalance + amt
      amountTotal += amt

      const daysVal = row.querySelector("[data-field='days']")?.value?.trim() || ""
      if (daysVal.includes("(")) {
        daysTotal += parseInt(daysVal.replace(/[()]/g, "")) * -1
      } else {
        daysTotal += parseInt(daysVal) || 0
      }
    })

    this._setFooterTotal(this.hasPcoModalContractTotalTarget && this.pcoModalContractTotalTarget, contractTotal)
    this._setFooterTotal(this.hasPcoModalPrevTotalTarget && this.pcoModalPrevTotalTarget, prevTotal)
    this._setFooterTotal(this.hasPcoModalTotalTarget && this.pcoModalTotalTarget, amountTotal)
    this._setFooterTotal(this.hasPcoModalCoTotalsTotalTarget && this.pcoModalCoTotalsTotalTarget, coTotalsTotal)
    this._setFooterTotal(this.hasPcoModalBalanceTotalTarget && this.pcoModalBalanceTotalTarget, balanceTotal)
    if (this.hasPcoModalDaysTotalTarget) this.pcoModalDaysTotalTarget.textContent = daysTotal || ""
  }

  _setFooterTotal(target, value) {
    if (!target) return
    target.textContent = value ? this._formatAmount(value) : ""
    const sym = target.previousElementSibling
    if (sym) sym.textContent = value ? this.currencyValue : ""
  }

  hideNewPcoModal() {
    this.newPcoModalTarget.classList.add("hidden")
    this._attachmentIds = []
    this._pendingUploads = 0
    this._mode = "pco"
    this._ccdData = null
    this._ccdNotifyUsers = []
    if (this.hasPcoFileListTarget) this.pcoFileListTarget.innerHTML = ""
    showIntercom()
  }

  stopPropagation(e) {
    e.stopPropagation()
  }

  // -- File upload --

  dragoverDropzone(event) {
    event.preventDefault()
    this.pcoDropzoneTarget.classList.add("border-brand", "bg-blue-50/50")
  }

  dragleaveDropzone() {
    this.pcoDropzoneTarget.classList.remove("border-brand", "bg-blue-50/50")
  }

  dropDropzone(event) {
    event.preventDefault()
    this.pcoDropzoneTarget.classList.remove("border-brand", "bg-blue-50/50")
    const files = Array.from(event.dataTransfer.files).filter(f =>
      f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")
    )
    if (files.length > 0) this._doPcoFileUpload(files)
  }

  clickDropzone() {
    this.pcoFileInputTarget.click()
  }

  selectFiles(event) {
    const files = Array.from(event.target.files)
    if (files.length > 0) this._doPcoFileUpload(files)
    event.target.value = ""
  }

  async _doPcoFileUpload(files) {
    const token = this._csrfToken
    this._pendingUploads++
    this._updateUploadState()

    const placeholders = files.map(file => {
      const chip = this._createFileChip(file.name, { uploading: true })
      this.pcoFileListTarget.appendChild(chip)
      return chip
    })

    try {
      const presignUrl = `/projects/${this.projectIdValue}/finances/presign_uploads`
      const presignRes = await fetch(presignUrl, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: files.map(f => ({
            filename: f.name,
            content_type: f.type || "application/pdf",
            byte_size: f.size
          }))
        })
      })

      if (!presignRes.ok) {
        const err = await presignRes.json().catch(() => ({}))
        throw new Error(err.error || "Failed to get upload URLs")
      }

      const { uploads } = await presignRes.json()

      const results = await Promise.allSettled(
        uploads.map(async (upload, i) => {
          const res = await fetch(upload.upload_url, {
            method: "PUT",
            headers: { "Content-Type": files[i].type || "application/pdf" },
            body: files[i]
          })
          if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`)
          return upload
        })
      )

      const successful = results.filter(r => r.status === "fulfilled").map(r => r.value)
      if (successful.length === 0) throw new Error("All uploads failed")

      const registerUrl = `/projects/${this.projectIdValue}/finances/register_uploads`
      const registerRes = await fetch(registerUrl, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: successful.map(u => ({ cache_name: u.cache_name, filename: u.filename }))
        })
      })

      const registered = await registerRes.json()

      placeholders.forEach(p => p.remove())
      registered.forEach(a => {
        const name = a.filename || `File #${a.id}`
        this._attachmentIds.push(a.id)
        const chip = this._createFileChip(name, {
          removable: true,
          onRemove: () => {
            chip.remove()
            this._attachmentIds = this._attachmentIds.filter(id => id !== a.id)
          }
        })
        this.pcoFileListTarget.appendChild(chip)
      })
    } catch (e) {
      console.error("File upload failed:", e)
      placeholders.forEach(p => p.remove())
      this._showToast("File upload failed", "alert")
    } finally {
      this._pendingUploads--
      this._updateUploadState()
    }
  }

  _updateUploadState() {
    const uploading = this._pendingUploads > 0
    if (this.hasSaveDraftBtnTarget) this._setButtonUploading(this.saveDraftBtnTarget, uploading)
    if (this.hasSignRequestBtnTarget) this._setButtonUploading(this.signRequestBtnTarget, uploading)
    if (this.hasAddPastPcoBtnTarget) this._setButtonUploading(this.addPastPcoBtnTarget, uploading)
  }

  _setButtonUploading(btn, uploading) {
    btn.disabled = uploading
    btn.classList.toggle("opacity-60", uploading)
    btn.classList.toggle("cursor-not-allowed", uploading)
    btn.classList.toggle("pointer-events-none", uploading)
    if (uploading) {
      if (!btn.dataset.originalText) {
        btn.dataset.originalText = btn.textContent
        btn.style.minWidth = `${btn.offsetWidth}px`
      }
      btn.innerHTML = `<svg class="animate-spin h-4 w-4 inline-block align-middle mr-1 -mt-px" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>Uploading\u2026`
    } else if (btn.dataset.originalText) {
      btn.textContent = btn.dataset.originalText
      delete btn.dataset.originalText
      btn.style.minWidth = ""
    }
  }

  _createFileChip(name, { removable = false, uploading = false, onRemove = null, url = null } = {}) {
    const chip = document.createElement(url ? "a" : "div")
    if (url) {
      chip.href = url
      chip.target = "_blank"
      chip.rel = "noopener"
    }
    chip.className = `inline-flex items-center gap-1.5 pl-2 ${removable ? "pr-1" : "pr-2"} py-1 rounded-md text-xs${url ? " cursor-pointer hover:opacity-80" : ""}`
    chip.classList.add(uploading ? "chip-uploading" : "chip-file")

    const icon = uploading
      ? `<svg class="w-3.5 h-3.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>`
      : `<svg class="w-3.5 h-3.5 shrink-0 chip-file-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/></svg>`

    const truncatedName = name.length > 28 ? name.slice(0, 24) + "..." + name.slice(name.lastIndexOf(".")) : name

    let removeBtn = ""
    if (removable) {
      removeBtn = `<button type="button" class="ml-0.5 p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors" data-remove-chip>
        <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>`
    }

    chip.innerHTML = `${icon}<span class="truncate max-w-[180px]" title="${this._escAttr(name)}">${truncatedName}</span>${removeBtn}`

    if (removable && onRemove) {
      const btn = chip.querySelector("[data-remove-chip]")
      btn.addEventListener("click", (e) => {
        e.stopPropagation()
        onRemove()
      })
    }

    return chip
  }

  // -- History Log --

  _renderHistoryLog(entries) {
    if (!this.hasPcoHistoryLogTarget) return
    this.pcoHistoryLogEntriesTarget.innerHTML = ""
    if (entries.length === 0) {
      this.pcoHistoryLogTarget.classList.add("hidden")
      return
    }
    entries.forEach(entry => {
      const p = document.createElement("p")
      p.className = "text-[12px] text-gray-400 leading-5"
      p.textContent = `${entry.datetime} - ${entry.username} (${entry.company_role}) ${entry.message}`
      this.pcoHistoryLogEntriesTarget.appendChild(p)
    })
    this.pcoHistoryLogTarget.classList.remove("hidden")
  }

  _resetModalHeaderBadges() {
    // Reset all header badge elements to a known hidden state so that
    // _renderModalHeaderBadges and _renderCcdModalHeaderBadges start clean.
    if (this.hasModalSignedBadgeTarget) { this.modalSignedBadgeTarget.classList.add("hidden"); this.modalSignedBadgeTarget.classList.remove("inline-flex") }
    if (this.hasModalSignedTextTarget) this.modalSignedTextTarget.classList.remove("hidden")
    if (this.hasModalSignedCheckTarget) this.modalSignedCheckTarget.classList.add("hidden")
    if (this.hasModalSignedTooltipTarget) this.modalSignedTooltipTarget.classList.add("hidden")
    if (this.hasModalPendingTextTarget) { this.modalPendingTextTarget.classList.add("hidden"); this.modalPendingTextTarget.textContent = "" }
    if (this.hasModalLinkedCoTarget) { this.modalLinkedCoTarget.classList.add("hidden"); this.modalLinkedCoTarget.classList.remove("inline-flex") }
    if (this.hasModalRequestApprovalBtnTarget) { this.modalRequestApprovalBtnTarget.classList.add("hidden"); this.modalRequestApprovalBtnTarget.classList.remove("inline-flex") }
  }

  _renderModalHeaderBadges(data) {
    this._resetModalHeaderBadges()
    const isEditing = !!this._editSequence
    const signedCount = data.signed_count || 0
    const tooltip = data.signature_tooltip || ""
    const showRequestBtn = data.show_request_approval_btn || false
    const isPast = data.is_past_payment || false
    const myRoleSigned = isEditing && data.my_role_signed
    const isVoid = this._isVoid
    const linkedCo = this._linkedCo
    const reqSigs = this._requiredSigs

    // Linked CO indicator
    if (this.hasModalLinkedCoTarget) {
      if (linkedCo) {
        this.modalLinkedCoTextTarget.textContent = `Added to CO#${linkedCo}`
        this.modalLinkedCoTarget.classList.remove("hidden")
        this.modalLinkedCoTarget.classList.add("inline-flex")
      } else {
        this.modalLinkedCoTarget.classList.add("hidden")
        this.modalLinkedCoTarget.classList.remove("inline-flex")
      }
    }

    // Request Approval button
    if (this.hasModalRequestApprovalBtnTarget) {
      if (isEditing && showRequestBtn && signedCount > 0 && !isVoid && !linkedCo) {
        this._pendingSignSequence = this._editSequence
        this.modalRequestApprovalBtnTarget.classList.remove("hidden")
        this.modalRequestApprovalBtnTarget.classList.add("inline-flex")
      } else {
        this.modalRequestApprovalBtnTarget.classList.add("hidden")
        this.modalRequestApprovalBtnTarget.classList.remove("inline-flex")
      }
    }

    // Signed badge
    if (this.hasModalSignedBadgeTarget) {
      if (isEditing && (signedCount > 0 || isPast || isVoid)) {
        if (isVoid) {
          this.modalSignedTextTarget.textContent = "VOID"
        } else if (isPast) {
          this.modalSignedTextTarget.textContent = "Past PCO"
        } else {
          this.modalSignedTextTarget.textContent = `${signedCount}/${reqSigs} Acknowledged`
        }
        this.modalSignedCheckTarget.classList.add("hidden")
        if (!isPast && !isVoid && tooltip) {
          this.modalSignedTooltipTarget.setAttribute("data-tip", tooltip)
          this.modalSignedTooltipTarget.setAttribute("data-tip-align", "left")
          this.modalSignedTooltipTarget.setAttribute("data-tip-pos", "below")
          this.modalSignedTooltipTarget.classList.remove("hidden")
        } else {
          this.modalSignedTooltipTarget.classList.add("hidden")
        }
        if (!isPast && !isVoid && myRoleSigned) this.modalSignedCheckTarget.classList.remove("hidden")
        const badge = this.modalSignedBadgeTarget
        badge.classList.remove("bg-green-100", "text-green-600", "bg-amber-100", "text-amber-600", "bg-gray-200", "text-gray-600", "bg-red-100", "text-red-600")
        if (isVoid) {
          badge.classList.add("bg-red-100", "text-red-600")
        } else if (isPast) {
          badge.classList.add("bg-gray-200", "text-gray-600")
        } else if (signedCount < reqSigs) {
          badge.classList.add("bg-amber-100", "text-amber-600")
        } else {
          badge.classList.add("bg-green-100", "text-green-600")
        }
        badge.classList.remove("hidden")
        badge.classList.add("inline-flex")
      } else {
        this.modalSignedBadgeTarget.classList.add("hidden")
        this.modalSignedBadgeTarget.classList.remove("inline-flex")
      }
    }

    // Delete button — contractor on unsigned drafts, OR owner on past PCOs; not void, not linked
    if (this.hasModalDeleteBtnTarget) {
      const showDelete = isEditing && !isVoid && ((this.isContractorValue && signedCount === 0 && !isPast && !linkedCo) || (isPast && this.isOwnerValue))
      this.modalDeleteBtnTarget.classList.toggle("hidden", !showDelete)
    }

    // Void button — contractor/consultant, editing, unsigned draft only, not void, not linked
    if (this.hasModalVoidBtnTarget) {
      const showVoid = isEditing && !isVoid && !linkedCo && !isPast && signedCount === 0 && (this.isContractorValue || this.isConsultantValue)
      this.modalVoidBtnTarget.classList.toggle("hidden", !showVoid)
    }

    // Unvoid button — contractor/owner, editing a voided PCO, not linked
    if (this.hasModalUnvoidBtnTarget) {
      const showUnvoid = isEditing && isVoid && !linkedCo && (this.isContractorValue || this.isOwnerValue)
      this.modalUnvoidBtnTarget.classList.toggle("hidden", !showUnvoid)
    }

    // Edit Past PCO button
    if (this.hasModalEditPastBtnTarget) {
      this.modalEditPastBtnTarget.classList.toggle("hidden", !(isPast && this.isOwnerValue))
    }

    // Pending text
    const contractorSigned = data.contractor_signed || false
    const consultantSigned = data.consultant_signed || false
    if (this.hasModalPendingTextTarget) {
      let pendingMsg = ""
      if (isEditing && !isPast && !isVoid && !linkedCo) {
        if (signedCount === 0) {
          pendingMsg = "Pending Contractor Signature"
        } else if (contractorSigned && !consultantSigned && this._requiredSigs >= 3) {
          pendingMsg = "Pending Consultant Signature"
        }
      }
      this.modalPendingTextTarget.textContent = pendingMsg
      this.modalPendingTextTarget.classList.toggle("hidden", !pendingMsg)
    }

    // Approve — consultant: when contractor signed; owner: when consultant signed (or no consultant required)
    // ownerApprover = owner, or a stakeholder granted PCO approval (PM Permissions)
    const ownerApprover = this.isOwnerValue || this.canApproveValue
    // An owner who signed without the consultant: say who.
    const skippedBy = isEditing ? data.consultant_skipped_by : null
    if (this.hasModalConsultantSkippedTarget) {
      const label = this.modalConsultantSkippedTarget
      label.textContent = skippedBy ? label.dataset.template.replace("%{who}", skippedBy) : ""
      label.classList.toggle("hidden", !skippedBy)
    }

    // "Sign without consultant" — owner side, contractor signed, consultant not yet
    if (this.hasModalSkipConsultantBtnTarget) {
      this.modalSkipConsultantBtnTarget.classList.toggle("hidden", !(isEditing && !isPast && !isVoid && !linkedCo && canSignWithoutConsultant({
        allowed: this.skipConsultantAllowedValue, ownerApprover, myRoleSigned,
        contractorSigned, consultantSigned, requiredSigs: this._requiredSigs
      })))
    }

    const canConsultantApprove = this.isConsultantValue && contractorSigned && !consultantSigned && !skippedBy
    const canOwnerApprove = ownerApprover && contractorSigned && (consultantSigned || this._requiredSigs <= 2) && !myRoleSigned
    const showApprove = (canConsultantApprove || canOwnerApprove) && isEditing && !isPast && !isVoid && !linkedCo
    if (this.hasModalApproveBtnTarget) this.modalApproveBtnTarget.classList.toggle("hidden", !showApprove)

    // Reject — consultant/owner, when at least 1 sig
    const showReject = (ownerApprover || this.isConsultantValue) && isEditing && signedCount >= 1 && !isPast && !isVoid && !linkedCo
    if (this.hasModalRejectBtnTarget) this.modalRejectBtnTarget.classList.toggle("hidden", !showReject)

    // Sign & Save Draft — contractor only, when not signed, not past, not void, not linked
    if (this.hasSignRequestBtnTarget) this.signRequestBtnTarget.classList.toggle("hidden", myRoleSigned || !this.isContractorValue || isPast || isVoid || !!linkedCo)
    if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.classList.toggle("hidden", myRoleSigned || !this.isContractorValue || isPast || isVoid || !!linkedCo)

    // Unsign — contractor: clears ALL sigs; consultant/owner: only theirs; only when signed and not linked
    const showUnsign = isEditing && myRoleSigned && !isPast && !isVoid && !linkedCo && signedCount < this._requiredSigs
    if (this.hasUnsignBtnTarget) this.unsignBtnTarget.classList.toggle("hidden", !showUnsign)

    // Withdraw all — contractor only, on a FULLY acknowledged PCO with no CO
    // linked (Phelipe 2026-09-11): every signature comes off and the PCO is a
    // draft again. Same server path as Unsign (a contractor unsign clears all).
    const showWithdrawAll = isEditing && this.isContractorValue && myRoleSigned && !isPast && !isVoid && !linkedCo && signedCount >= this._requiredSigs
    if (this.hasWithdrawAllBtnTarget) this.withdrawAllBtnTarget.classList.toggle("hidden", !showWithdrawAll)

    // Lock fields when signed, void, linked, past, or non-contractor viewing
    const locked = isVoid || !!linkedCo || myRoleSigned || isPast || (!this.isContractorValue && isEditing)
    this._setModalReadonly(locked)
  }

  _setModalReadonly(locked) {
    this.pcoTitleTarget.readOnly = locked
    this.pcoTitleTarget.classList.toggle("bg-yellow-50", !locked)
    this.pcoTitleTarget.classList.toggle("bg-transparent", locked)

    this.pcoLineItemsTarget.querySelectorAll("[data-field='comment'], [data-field='amount'], [data-field='days']").forEach(input => {
      input.readOnly = locked
      const td = input.closest("td")
      if (td) td.classList.toggle("bg-amber-50", !locked)
      // Hide $ sign on empty amount cells when locked
      if (input.dataset.field === "amount") {
        const sym = td?.querySelector("span.select-none")
        if (sym) {
          const hasValue = input.value.trim() !== ""
          sym.textContent = (!locked || hasValue) ? this.currencyValue : ""
        }
      }
    })

    if (this.hasPcoDropzoneTarget) this.pcoDropzoneTarget.classList.toggle("hidden", locked)
    if (this.hasUploadLabelTarget) this.uploadLabelTarget.classList.toggle("hidden", locked)

    if (this.hasPcoFileListTarget) {
      this.pcoFileListTarget.querySelectorAll("[data-remove-chip]").forEach(btn => {
        btn.classList.toggle("hidden", locked)
      })
    }
  }

  // -- Page card DOM helpers --

  _updateOverallTotals(overallTotal, overallDays) {
    if (this.hasOverallAmountTarget) {
      this.overallAmountTarget.innerHTML = `<b>${this._formatAmount(overallTotal)}</b>`
    }
    if (this.hasOverallDaysTarget) {
      this.overallDaysTarget.textContent = overallDays
    }
    if (this.hasOverallTotalsTarget) {
      this.overallTotalsTarget.classList.remove("hidden")
    }
  }

  _buildBadgeHTML(data) {
    const signedCount = data.signed_count || 0
    const isPast = data.is_past_payment || false
    const isVoid = data.is_void || false
    const tooltip = data.signature_tooltip || ""
    const myRoleSigned = data.my_role_signed || false
    const reqSigs = data.required_sigs || this._requiredSigs
    const linkedCo = data.linked_co || null

    let html = ""

    if (isVoid) {
      html += `<span class="inline-flex items-center gap-1 bg-red-100 text-red-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px]">VOID</span>`
    } else if (isPast) {
      html += `<span class="inline-flex items-center gap-1 bg-gray-200 text-gray-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px]">Past PCO</span>`
    } else if (signedCount > 0) {
      const badgeBg = signedCount < reqSigs ? "bg-amber-100 text-amber-600" : "bg-green-100 text-green-600"
      let iconHtml = ""
      if (signedCount >= reqSigs && myRoleSigned) iconHtml = SVG_CHECK
      let tooltipHtml = ""
      if (tooltip) tooltipHtml = `<span class="shrink-0 cursor-help flex items-center" data-tip="${this._escAttr(tooltip)}" data-tip-align="left" data-tip-pos="below">${SVG_INFO_CIRCLE}</span>`
      html += `<span class="inline-flex items-center gap-1 ${badgeBg} text-[12px] font-medium rounded-lg px-2.5 py-[2px]">${iconHtml} ${signedCount}/${reqSigs} Acknowledged ${tooltipHtml}</span>`
    } else {
      html += `<span class="inline-flex items-center gap-1 bg-gray-200 text-gray-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px]">DRAFT</span>`
    }

    if (linkedCo) {
      html += ` <span class="inline-flex items-center gap-1 bg-blue-50 text-blue-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px]">${SVG_LINK} Added to CO#${linkedCo}</span>`
    }

    return html
  }

  _buildFullCardHTML(data) {
    const seq = data.sequence
    const isPast = data.is_past_payment || false
    const isVoid = data.is_void || false
    const pdfUrl = data.final_pdf_url || `/projects/${this.projectIdValue}/finances/pco_export_with_pdfs/${seq}.pdf`
    const pcoTotal = data.pco_total || 0
    const cur = this.currencyValue
    const titleHTML = data.title ? `<div data-pco-title class="px-3 py-1 text-[13px] text-gray-600 border-t border-gray-200"><span class="font-medium">Title:</span> ${this._escapeHTML(data.title)}</div>` : ""

    return `<div class="pco-card bg-white border border-gray-200 rounded-lg" data-sequence="${seq}" data-signed-count="${data.signed_count || 0}" data-is-past="${isPast}" data-is-void="${isVoid}">
      <div class="flex items-center justify-between px-3 py-2.5 text-[13px] text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer" data-action="click->proposed-change-order#editPco" data-sequence="${seq}">
        <div class="flex items-center gap-3">
          <span class="font-semibold text-gray-700 text-[14px]">PCO #${seq}</span>
          <span data-badge-area>${this._buildBadgeHTML(data)}</span>
        </div>
        <div class="flex items-center gap-3">
          <span data-export-area>
            <div class="relative" data-controller="dropdown">
              <button data-action="click->dropdown#toggle" class="inline-flex items-center gap-1 bg-gray-200 hover:bg-gray-300 text-gray-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px] transition-colors cursor-pointer">Export <svg class="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 10 6"><path d="M0 0l5 6 5-6z"/></svg></button>
              <div data-dropdown-target="menu" class="hidden absolute right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-30 min-w-[100px] py-1">
                <a href="${pdfUrl}" target="_blank" data-action="click->proposed-change-order#stopPropagation" class="block px-3 py-1 text-[13px] text-gray-700 hover:bg-gray-50">PDF</a>
              </div>
            </div>
          </span>
          <span data-last-updated class="text-gray-400 text-[12px] italic">Last updated on ${data.last_updated_at || ""} by ${data.who_updated || ""}</span>
          <span data-card-total-amount class="font-semibold text-gray-700 text-[14px]">${cur}${this._formatAmount(pcoTotal)}</span>
        </div>
      </div>
      ${titleHTML}
      ${this._buildLineItemsTableHTML(data)}
    </div>`
  }

  _buildLineItemsTableHTML(data) {
    const cur = this.currencyValue
    const lineItems = data.line_items || []
    if (lineItems.length === 0) return ""

    let rows = ""
    lineItems.forEach(li => {
      if (li.line_item_id != null) {
        const amt = parseFloat(li.amount) || 0
        const amtStr = amt < 0
          ? `${cur}(${this._formatAmount(Math.abs(amt))})`
          : `${cur}${this._formatAmount(amt)}`
        const daysVal = this._parseDaysStr(li.days)
        const daysStr = daysVal < 0
          ? `<span class="text-red-600">(${Math.abs(daysVal)})</span>`
          : `${daysVal}`
        rows += `<tr class="bg-white border-b border-l border-r border-gray-200">
          <td class="px-2 py-1 border-r border-gray-200">${this._escapeHTML(li.item || "")}</td>
          <td class="px-2 py-1 border-r border-gray-200">${this._escapeHTML(li.csi_div || "")}</td>
          <td class="px-2 py-1">${this._escapeHTML(li.description || "")}</td>
          <td class="px-1 py-1"></td>
          <td class="px-2 py-1 border-l border-gray-200">${this._escapeHTML(li.comment || "")}</td>
          <td class="px-2 py-1 text-right border-l border-r border-gray-200 ${amt < 0 ? "text-red-600" : ""}">${amtStr}</td>
          <td class="px-2 py-1 text-right">${daysStr}</td>
        </tr>`
      } else if (li.is_fee && (parseFloat(li.amount) !== 0 || parseInt(li.days) !== 0)) {
        const amt = parseFloat(li.amount) || 0
        const daysVal = parseInt(li.days) || 0
        rows += `<tr class="bg-white border-b border-l border-r border-gray-200">
          <td class="px-2 py-1 border-r border-gray-200"></td>
          <td class="px-2 py-1 border-r border-gray-200"></td>
          <td class="px-2 py-1">Contractor's Fee</td>
          <td class="px-1 py-1"></td>
          <td class="px-2 py-1 border-l border-gray-200">${this._escapeHTML(li.comment || "")}</td>
          <td class="px-2 py-1 text-right border-l border-r border-gray-200">${cur}${this._formatAmount(amt)}</td>
          <td class="px-2 py-1 text-right">${daysVal}</td>
        </tr>`
      }
    })

    const total = data.pco_total || 0
    const totalDays = data.pco_days || 0
    const totalAmtStr = total < 0
      ? `${cur}(${this._formatAmount(Math.abs(total))})`
      : `${cur}${this._formatAmount(total)}`
    const totalDaysStr = totalDays < 0
      ? `<span class="text-red-600">(${Math.abs(totalDays)})</span>`
      : `${totalDays}`
    rows += `<tr class="bg-white border-b border-l border-r border-gray-200 font-semibold">
      <td colspan="5" class="px-2 py-1 text-right border-r border-gray-200">Total</td>
      <td class="px-2 py-1 text-right border-l border-r border-gray-200 ${total < 0 ? "text-red-600" : ""}">${totalAmtStr}</td>
      <td class="px-2 py-1 text-right">${totalDaysStr}</td>
    </tr>`

    return `<table data-pco-table class="w-full text-xs text-gray-600 border-collapse" style="table-layout:fixed">
      <colgroup>
        <col style="width:50px">
        <col style="width:65px">
        <col style="width:320px">
        <col style="width:24px">
        <col>
        <col style="width:150px">
        <col style="width:80px">
      </colgroup>
      <thead>
        <tr class="bg-white border border-gray-200">
          <th class="px-2 py-1 font-medium text-gray-500 border-r border-gray-200 text-left whitespace-nowrap">Item</th>
          <th class="px-2 py-1 font-medium text-gray-500 border-r border-gray-200 text-left whitespace-nowrap">CSI Div</th>
          <th class="px-2 py-1 font-medium text-gray-500 text-left">Work</th>
          <th></th>
          <th class="px-2 py-1 font-medium text-gray-500 border-l border-gray-200 text-left">Comments</th>
          <th class="px-2 py-1 font-medium text-gray-500 border-l border-r border-gray-200 text-right">Amount</th>
          <th class="px-2 py-1 font-medium text-gray-500 text-right whitespace-nowrap">Days</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>`
  }

  _parseDaysStr(daysStr) {
    if (!daysStr) return 0
    const s = String(daysStr)
    if (s.includes("(")) return parseInt(s.replace(/[()]/g, "")) * -1
    return parseInt(s) || 0
  }

  _escapeHTML(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  _updatePageCard(data) {
    const card = this.element.querySelector(`.pco-card[data-sequence="${data.sequence}"]`)
    if (!card) return
    card.dataset.signedCount = data.signed_count || 0
    card.dataset.isPast = data.is_past_payment || false
    card.dataset.isVoid = data.is_void || false
    const badgeArea = card.querySelector("[data-badge-area]")
    if (badgeArea) badgeArea.innerHTML = this._buildBadgeHTML(data)
    const lastUpdated = card.querySelector("[data-last-updated]")
    if (lastUpdated && data.last_updated_at) {
      lastUpdated.textContent = `Last updated on ${data.last_updated_at} by ${data.who_updated || ""}`
    }
    const totalAmount = card.querySelector("[data-card-total-amount]")
    if (totalAmount) {
      totalAmount.textContent = `${this.currencyValue}${this._formatAmount(data.pco_total || 0)}`
    }
    // Update title row
    const existingTitle = card.querySelector("[data-pco-title]")
    if (data.title) {
      const titleHTML = `<div data-pco-title class="px-3 py-1 text-[13px] text-gray-600 border-t border-gray-200"><span class="font-medium">Title:</span> ${this._escapeHTML(data.title)}</div>`
      if (existingTitle) {
        existingTitle.outerHTML = titleHTML
      } else {
        const header = card.querySelector("[data-action*='editPco']")
        if (header) header.insertAdjacentHTML("afterend", titleHTML)
      }
    } else if (existingTitle) {
      existingTitle.remove()
    }
    // Rebuild line items table
    const existingTable = card.querySelector("[data-pco-table]")
    const newTableHTML = this._buildLineItemsTableHTML(data)
    if (existingTable) {
      existingTable.outerHTML = newTableHTML
    } else if (newTableHTML) {
      card.insertAdjacentHTML("beforeend", newTableHTML)
    }
  }

  _insertNewPageCard(data) {
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
    const cardsContainer = this.hasPcoCardsTarget ? this.pcoCardsTarget : null
    if (!cardsContainer) return
    cardsContainer.classList.add("flex", "flex-col", "gap-5")

    // New PCO is the most recent item — insert at the top
    cardsContainer.insertAdjacentHTML("afterbegin", this._buildFullCardHTML(data))
  }

  _removePageCard(sequence) {
    const card = this.element.querySelector(`.pco-card[data-sequence="${sequence}"]`)
    if (card) card.remove()
  }

  _updateModalState(data) {
    this._renderModalHeaderBadges(data)
    this._renderHistoryLog(data.history_log || [])
  }

  // -- Add Past PCO --

  async addPastPco() {
    this._clearValidationErrors()
    let valid = true

    if (!this.pcoTitleTarget.value.trim()) {
      this._markFieldError(this.pcoTitleTarget)
      valid = false
    }

    let missingComments = false
    this.pcoLineItemsTarget.querySelectorAll("tr").forEach(row => {
      const amount = this._parseCurrency(row.querySelector("[data-field='amount']")?.value)
      const days = row.querySelector("[data-field='days']")?.value?.trim() || ""
      const comment = row.querySelector("[data-field='comment']")?.value?.trim() || ""
      const commentInput = row.querySelector("[data-field='comment']")
      if ((amount !== 0 || days) && !comment) {
        missingComments = true
        if (commentInput) this._markFieldError(commentInput)
      }
    })

    if (!valid || missingComments) {
      this._showToast("Please enter a Title, and ensure all rows with a value in Amount or Days include a Comment.", "alert")
      return
    }

    const lineItemsArr = this._collectPcoLineItems()
    if (lineItemsArr.items.length === 0 && !lineItemsArr.fee) {
      this._showToast("At least one line item with amount or days is required.", "alert")
      return
    }

    if (this._attachmentIds.length === 0) {
      this._showToast("At least one PDF is required for a Past PCO", "alert")
      return
    }

    // Check for negative balances (credit exceeding available balance)
    let hasNegativeBalance = false
    this.pcoLineItemsTarget.querySelectorAll("tr").forEach(row => {
      if (row.dataset.isFee === "true") return
      const baseBalance = parseFloat(row.querySelector("[data-base-balance]")?.dataset.baseBalance) || 0
      const amount = this._parseCurrency(row.querySelector("[data-field='amount']")?.value)
      if (Math.round((baseBalance + amount) * 100) / 100 < 0) hasNegativeBalance = true
    })
    // A Past PCO records what already happened outside ConsLog, so the owner
    // can push a credit past the line's remaining balance (Phelipe
    // 2026-08-06) — they still see the warning, on the confirmation step,
    // and choose to go ahead. Everyone else is still stopped.
    if (hasNegativeBalance && !this.isOwnerValue) {
      this._showToast("One or more line items have an amount above the available balance. Please correct the values highlighted in red and try again.", "alert", 10000)
      return
    }

    const nextSequence = this._getNextPcoSequence()
    this._overBalanceResume = null
    if (this.hasPastPcoTitlePrefixTarget) {
      this.pastPcoTitlePrefixTarget.textContent = this.pastPcoTitlePrefixTarget.dataset.createText
    }
    if (this.hasPastPcoConfirmBodyTarget) {
      this.pastPcoConfirmBodyTarget.textContent = this.pastPcoConfirmBodyTarget.dataset.createText
    }
    if (this.hasPastPcoSequenceLabelTarget) {
      this.pastPcoSequenceLabelTarget.textContent = `PCO#${nextSequence}`
    }
    if (this.hasPastPcoNegativeWarningTarget) {
      this.pastPcoNegativeWarningTarget.classList.toggle("hidden", !hasNegativeBalance)
    }
    this.pastPcoConfirmModalTarget.classList.remove("hidden")
  }

  async confirmPastPco() {
    this.pastPcoConfirmModalTarget.classList.add("hidden")
    // Resuming an owner's over-balance EDIT of a past PCO — rerun the same
    // save with validation skipped, exactly like the create flow below.
    if (this._overBalanceResume) {
      const { signAfter } = this._overBalanceResume
      this._overBalanceResume = null
      this._skipValidation = true
      await this._submitPco(signAfter)
      this._skipValidation = false
      return
    }
    this._isPast = true
    this._skipValidation = true
    await this._submitPco(false)
    this._skipValidation = false
  }

  hidePastPcoConfirmModal() {
    this.pastPcoConfirmModalTarget.classList.add("hidden")
    this._overBalanceResume = null
  }

  // The shared confirmation modal, re-labeled for an edit: "Updating
  // PCO#N …", edit wording, warning always on (that's why we're here).
  _openPastPcoOverBalanceConfirm(signAfter) {
    this._overBalanceResume = { signAfter }
    if (this.hasPastPcoTitlePrefixTarget) {
      this.pastPcoTitlePrefixTarget.textContent = this.pastPcoTitlePrefixTarget.dataset.editText
    }
    if (this.hasPastPcoConfirmBodyTarget) {
      this.pastPcoConfirmBodyTarget.textContent = this.pastPcoConfirmBodyTarget.dataset.editText
    }
    if (this.hasPastPcoSequenceLabelTarget) {
      this.pastPcoSequenceLabelTarget.textContent = `PCO#${this._editSequence}`
    }
    if (this.hasPastPcoNegativeWarningTarget) {
      this.pastPcoNegativeWarningTarget.classList.remove("hidden")
    }
    this.pastPcoConfirmModalTarget.classList.remove("hidden")
  }

  // -- Save PCO --

  async saveDraft() {
    if (this._mode === "ccd") {
      await this._submitCcd()
    } else {
      await this._submitPco(false)
    }
  }
  async signAndRequest() { await this._submitPco(true) }

  _validatePco(signAfter) {
    this._clearValidationErrors()
    let valid = true

    if (!this.pcoTitleTarget.value.trim()) {
      this._markFieldError(this.pcoTitleTarget)
      valid = false
    }

    let missingComments = false
    this.pcoLineItemsTarget.querySelectorAll("tr").forEach(row => {
      const amount = this._parseCurrency(row.querySelector("[data-field='amount']")?.value)
      const days = row.querySelector("[data-field='days']")?.value?.trim() || ""
      const comment = row.querySelector("[data-field='comment']")?.value?.trim() || ""
      const commentInput = row.querySelector("[data-field='comment']")
      if ((amount !== 0 || days) && !comment) {
        missingComments = true
        if (commentInput) this._markFieldError(commentInput)
      }
    })

    if (!valid && missingComments) {
      this._showToast("Please enter a Title, and ensure all rows with a value in Amount or Days include a Comment.", "alert")
      return false
    } else if (!valid) {
      this._showToast("Title is required.", "alert")
      return false
    } else if (missingComments) {
      this._showToast("All rows with a value in Amount or Days must include a Comment.", "alert")
      return false
    }

    // Amount format validation
    let badFormat = false
    this.pcoLineItemsTarget.querySelectorAll("[data-field='amount']").forEach(el => {
      const raw = el.value.trim()
      if (!raw) return
      let cleaned = raw
      if (this.currencyFormatValue === "european_currency_format") {
        cleaned = cleaned.replace(/\./g, "").replace(",", ".")
      } else if (this.currencyFormatValue === "swiss_currency_format") {
        cleaned = cleaned.replace(/'/g, "")
      } else {
        cleaned = cleaned.replace(/,/g, "")
      }
      cleaned = cleaned.replace(this.currencyValue, "").trim()
      if (!/^\(?-?\d*\.?\d*\)?$/.test(cleaned) || cleaned === "" || cleaned === "." || cleaned === "()" || cleaned === "-.") {
        badFormat = true
        this._markFieldError(el)
      }
    })
    if (badFormat) {
      this._showToast("One or more amount fields are formatted incorrectly. Use numbers like 100.50, -100.50, or (100.50).", "alert")
      return false
    }

    // Check for negative balances (credit exceeding available balance)
    let hasNegativeBalance = false
    this.pcoLineItemsTarget.querySelectorAll("tr").forEach(row => {
      if (row.dataset.isFee === "true") return
      const baseBalance = parseFloat(row.querySelector("[data-base-balance]")?.dataset.baseBalance) || 0
      const amount = this._parseCurrency(row.querySelector("[data-field='amount']")?.value)
      if (Math.round((baseBalance + amount) * 100) / 100 < 0) hasNegativeBalance = true
    })
    if (hasNegativeBalance) {
      // EDITING a past PCO gets the same owner-only allowance as adding one
      // (Phelipe 2026-08-19): the same confirmation modal warns about the
      // over-balance lines and the owner may proceed anyway.
      if (this.isOwnerValue && this._isPast) {
        this._openPastPcoOverBalanceConfirm(signAfter)
        return false
      }
      this._showToast("One or more line items have an amount above the available balance. Please correct the values highlighted in red and try again.", "alert", 10000)
      return false
    }

    if (signAfter) {
      let hasAmountOrDays = false
      this.pcoLineItemsTarget.querySelectorAll("[data-field='amount']").forEach(el => {
        if (this._parseCurrency(el.value) !== 0) hasAmountOrDays = true
      })
      this.pcoLineItemsTarget.querySelectorAll("[data-field='days']").forEach(el => {
        if (el.value.trim() !== "" && parseInt(el.value.trim()) !== 0) hasAmountOrDays = true
      })
      if (!hasAmountOrDays) {
        this._showToast("At least one line item must have an amount or days.", "alert")
        return false
      }

      if (this._attachmentIds.length === 0) {
        this._showToast("At least one PDF is required to sign.", "alert")
        return false
      }

    }

    return true
  }

  _markFieldError(input) {
    input.classList.add("!bg-red-100")
    input.addEventListener("input", () => input.classList.remove("!bg-red-100"), { once: true })
  }

  _clearValidationErrors() {
    this.newPcoModalTarget.querySelectorAll(".\\!bg-red-100").forEach(el => {
      el.classList.remove("!bg-red-100")
    })
  }

  async _submitPco(signAfter) {
    if (this._submitting) return

    const collected = this._collectPcoLineItems()
    if (collected.items.length === 0 && !collected.fee && !signAfter) return

    if (!this._skipValidation && !this._validatePco(signAfter)) return

    if (collected.items.length === 0 && !collected.fee) return

    this._submitting = true
    if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.disabled = true
    if (this.hasSignRequestBtnTarget) this.signRequestBtnTarget.disabled = true

    try {

    const sequence = this._editSequence || this._getNextPcoSequence()
    const isUpdate = !!this._editSequence

    let action
    if (isUpdate) {
      action = this._isPast ? "update_past_proposed_change_order_line_items" : "update_proposed_change_order_line_items"
    } else {
      action = this._isPast ? "create_past_pco_line_items" : "create_proposed_change_order_line_items"
    }

    const params = {
      sequence: sequence,
      line_items_arr: JSON.stringify(collected.items),
      fee_amount: collected.fee ? collected.fee.amount : "",
      fee_days: collected.fee ? collected.fee.days : "",
      fee_comment: collected.fee ? collected.fee.comment : "",
      title: this.pcoTitleTarget.value,
      attachment_ids: this._attachmentIds.join(" ")
    }

    const method = isUpdate ? "PUT" : "POST"
    const saveResp = await this._ajax(method, action, params)

    if (signAfter && !this._isPast) {
      this._pendingSignSequence = sequence
      this._signFromEditModal = true
      this._signFromEditIsNew = !isUpdate
      this._signFromEditSaveResp = saveResp
      this.signModalTarget.classList.remove("hidden")
      return
    }

    if (saveResp) {
      if (isUpdate) {
        this._updatePageCard(saveResp)
      } else {
        this._insertNewPageCard(saveResp)
      }
      this._updateOverallTotals(saveResp.overall_total, saveResp.overall_days)
    }

    this.hideNewPcoModal()
    this._showToast("Saved", "success")
    this._startPdfPolling(sequence, "PCO")
    } finally {
      this._submitting = false
      if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.disabled = false
      if (this.hasSignRequestBtnTarget) this.signRequestBtnTarget.disabled = false
    }
  }

  _collectPcoLineItems() {
    const items = []
    let fee = null
    this.pcoLineItemsTarget.querySelectorAll("tr").forEach(row => {
      const isFee = row.dataset.isFee === "true"
      const amount = this._parseCurrency(row.querySelector("[data-field='amount']")?.value)
      const days = row.querySelector("[data-field='days']")?.value || ""
      const comment = row.querySelector("[data-field='comment']")?.value || ""

      if (isFee) {
        if (amount !== 0 || days || comment) {
          fee = { amount, days, comment }
        }
      } else {
        const lineItemId = row.dataset.lineItemId
        if (amount !== 0 || days || comment) {
          items.push([lineItemId, amount, days, comment])
        }
      }
    })
    return { items, fee }
  }

  _getNextPcoSequence() {
    const pcoElements = this.element.querySelectorAll(".pco-card[data-sequence]")
    let max = 0
    pcoElements.forEach(el => {
      const seq = parseInt(el.dataset.sequence)
      if (seq > max) max = seq
    })
    return max + 1
  }

  // -- Delete --

  showDeleteModal() { this.deleteModalTarget.classList.remove("hidden") }
  hideDeleteModal() { this.deleteModalTarget.classList.add("hidden") }

  async confirmDelete() {
    const resp = await this._ajax("DELETE", "destroy_proposed_change_order_line_items", { sequence: this._pendingDeleteSequence })
    this.hideDeleteModal()
    this.hideNewPcoModal()
    this._removePageCard(this._pendingDeleteSequence)

    if (resp && resp.has_any_pcos) {
      this._updateOverallTotals(resp.overall_total, resp.overall_days)
    } else {
      if (this.hasOverallTotalsTarget) this.overallTotalsTarget.classList.add("hidden")
      if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove("hidden")
    }
    this._showToast("Deleted", "success")
  }

  // -- Void --

  showVoidModal() {
    if (this._mode === "ccd") { this.voidCcd(); return }
    this.voidModalTarget.classList.remove("hidden")
  }
  hideVoidModal() { this.voidModalTarget.classList.add("hidden") }

  async confirmVoid() {
    const resp = await this._ajax("POST", "void_proposed_change_order_line_items", { sequence: this._editSequence })
    this.hideVoidModal()
    if (resp) {
      this._updatePageCard(resp)
      this._updateOverallTotals(resp.overall_total, resp.overall_days)
    }
    this.hideNewPcoModal()
    this._showToast("PCO voided", "success")
    this._startPdfPolling(this._editSequence, "PCO")
  }

  // -- Unvoid --

  showUnvoidModal() {
    if (this._mode === "ccd") { this.unvoidCcd(); return }
    this.unvoidModalTarget.classList.remove("hidden")
  }
  hideUnvoidModal() { this.unvoidModalTarget.classList.add("hidden") }

  async confirmUnvoid() {
    const resp = await this._ajax("POST", "unvoid_proposed_change_order_line_items", { sequence: this._editSequence })
    this.hideUnvoidModal()
    if (resp) {
      this._updatePageCard(resp)
      this._updateOverallTotals(resp.overall_total, resp.overall_days)
    }
    this.hideNewPcoModal()
    this._showToast("PCO unvoided", "success")
    this._startPdfPolling(this._editSequence, "PCO")
  }

  // -- Sign / Unsign --

  showSignModal(sequence) {
    this._pendingSignSequence = sequence
    this.signModalTarget.classList.remove("hidden")
  }
  hideSignModal() {
    setSkipConsultantMode(this, false)
    if (this._signFromEditModal || this._approveAfterAcknowledge) {
      this.signModalTarget.classList.add("hidden")
      this._signFromEditModal = false
      this._signFromEditIsNew = false
      this._signFromEditSaveResp = null
      this._approveAfterAcknowledge = false
      return
    }
    this.signModalTarget.classList.add("hidden")
  }

  async confirmSign(event) {
    // One request per gesture — key-repeat (held Enter) or double-clicks fire
    // this repeatedly while the request is still in flight.
    if (this._confirmInFlight) return
    this._confirmInFlight = true
    const confirmBtn = event?.currentTarget
    if (confirmBtn) confirmBtn.disabled = true
    try {
      await this._confirmSignInner()
    } finally {
      this._confirmInFlight = false
      if (confirmBtn) confirmBtn.disabled = false
    }
  }

  async _confirmSignInner() {
    const fromEditModal = this._signFromEditModal
    const isNew = this._signFromEditIsNew
    const fromApprove = this._approveAfterAcknowledge
    this._signFromEditModal = false
    this._signFromEditIsNew = false
    this._signFromEditSaveResp = null
    this._approveAfterAcknowledge = false

    const resp = await this._ajax("POST", "sign_pco", withSkipConsultant(this, { sequence: this._pendingSignSequence }))
    this.signModalTarget.classList.add("hidden")

    if (fromApprove) {
      if (resp) {
        this._updatePageCard(resp)
        this._updateModalState(resp)
      }
      this.hideNewPcoModal()
      this._showToast("Acknowledged", "success")
      this._startPdfPolling(this._pendingSignSequence, "PCO")
      return
    }

    if (fromEditModal) {
      if (resp) {
        if (isNew) {
          this._insertNewPageCard(resp)
        } else {
          this._updatePageCard(resp)
        }
        this._updateOverallTotals(resp.overall_total, resp.overall_days)
        this._updateModalState(resp)
      }

      if (this.isContractorValue) {
        this._signedBeforeRequest = true
        this.requestApprovalModalTarget.classList.remove("hidden")
      } else {
        this.hideNewPcoModal()
      }
      this._showToast("Acknowledged", "success")
      this._startPdfPolling(this._pendingSignSequence, "PCO")
      return
    }

    if (resp) this._updatePageCard(resp)
    this._showToast("Acknowledged", "success")
    this._startPdfPolling(this._pendingSignSequence, "PCO")
  }

  unsignFromModal() {
    this._pendingSignSequence = this._editSequence
    this.unsignModalTarget.classList.remove("hidden")
  }

  showUnsignModal(sequence) {
    this._pendingSignSequence = sequence
    this.unsignModalTarget.classList.remove("hidden")
  }
  hideUnsignModal() { this.unsignModalTarget.classList.add("hidden") }

  async confirmUnsign() {
    const resp = await this._ajax("POST", "unsign_pco", { sequence: this._pendingSignSequence })
    this.hideUnsignModal()
    if (resp) {
      this._updatePageCard(resp)
      this._updateModalState(resp)
    }
    this._showToast("Signature removed", "success")
    this._startPdfPolling(this._pendingSignSequence, "PCO")
  }

  // -- Withdraw all signatures & save as draft (contractor, fully signed PCO) --

  withdrawAllFromModal() {
    this._pendingSignSequence = this._editSequence
    this.withdrawAllModalTarget.classList.remove("hidden")
  }
  hideWithdrawAllModal() { this.withdrawAllModalTarget.classList.add("hidden") }

  async confirmWithdrawAll() {
    const resp = await this._ajax("POST", "unsign_pco", { sequence: this._pendingSignSequence, withdraw_all: "1" })
    this.hideWithdrawAllModal()
    if (resp && resp.error) { this._showToast(resp.error, "alert"); return }
    if (resp) {
      this._updatePageCard(resp)
      this._updateModalState(resp)
    }
    this._showToast("Signatures withdrawn — this PCO is a draft again", "success")
    this._startPdfPolling(this._pendingSignSequence, "PCO")
  }

  // -- Edit Past PCO from modal --

  editPastPco() {
    this._setModalReadonly(false)
    if (this.hasModalEditPastBtnTarget) this.modalEditPastBtnTarget.classList.add("hidden")
    if (this.hasSaveDraftBtnTarget) {
      this.saveDraftBtnTarget.classList.remove("hidden")
      this.saveDraftBtnTarget.textContent = "Update Past PCO"
    }
  }

  // -- Delete from modal --

  deleteFromModal() {
    if (this._mode === "ccd") {
      this.showDeleteCcdModal()
      return
    }
    const cards = this.element.querySelectorAll(".pco-card[data-sequence]")
    let maxSeq = 0
    cards.forEach(el => { const s = parseInt(el.dataset.sequence); if (s > maxSeq) maxSeq = s })
    if (parseInt(this._editSequence) !== maxSeq) {
      this._showToast("Must delete the most recent item first. To delete this item, please first delete any more recent records.", "alert")
      return
    }
    this._pendingDeleteSequence = this._editSequence
    this.deleteModalTarget.classList.remove("hidden")
  }

  // -- Approve / Reject --

  // The owner's acknowledge pop-up, in "without the consultant" mode.
  signWithoutConsultant() {
    this.approvePco()
    setSkipConsultantMode(this, true)
  }

  approvePco() {
    this._pendingSignSequence = this._editSequence
    this._approveAfterAcknowledge = true
    this.signModalTarget.classList.remove("hidden")
  }

  showRejectPcoModal() { this.rejectPcoModalTarget.classList.remove("hidden") }
  hideRejectPcoModal() {
    this.rejectPcoModalTarget.classList.add("hidden")
    if (this.hasRejectCommentTarget) this.rejectCommentTarget.value = ""
  }

  async confirmRejectPco() {
    const comment = this.hasRejectCommentTarget ? this.rejectCommentTarget.value : ""
    const resp = await this._ajax("POST", "reject_pco", { sequence: this._editSequence, comment })
    this.hideRejectPcoModal()
    if (resp) {
      this._updatePageCard(resp)
      this._updateModalState(resp)
    }
    this.hideNewPcoModal()
    this._showToast("Rejected", "success")
  }

  // -- Request approval --

  showRequestApprovalModal() { this.requestApprovalModalTarget.classList.remove("hidden") }
  hideRequestApprovalModal() {
    this.requestApprovalModalTarget.classList.add("hidden")
    if (this._signedBeforeRequest) {
      this._signedBeforeRequest = false
      this.hideNewPcoModal()
    }
  }

  async sendApprovalRequest() {
    const sections = this.requestApprovalModalTarget.querySelectorAll("[data-company-section]")
    for (const section of sections) {
      if (section.classList.contains("hidden")) continue
      const checked = section.querySelectorAll("input[name='approval_email']:checked")
      if (checked.length === 0) {
        this._showToast("Please select at least one person from each company.", "alert")
        return
      }
    }

    const checkboxes = this.requestApprovalModalTarget.querySelectorAll("input[name='approval_email']:checked")
    const emails = Array.from(checkboxes).map(cb => cb.dataset.email)
    if (emails.length === 0) return

    const resp = await this._ajax("POST", "request_pco_approval", {
      sequence: this._pendingSignSequence,
      emails: JSON.stringify(emails)
    })
    if (resp) this._updatePageCard(resp)
    this._signedBeforeRequest = false
    this.requestApprovalModalTarget.classList.add("hidden")
    this.hideNewPcoModal()
    this._showToast("Approval request sent", "success")
  }

  // -- PDF polling --

  _startPdfPolling(sequence, docType) {
    if (this._pdfPollingTimers[sequence]) clearTimeout(this._pdfPollingTimers[sequence])
    this._setExportGenerating(sequence)

    const triggeredAt = Math.floor(Date.now() / 1000)
    const maxPollTime = 90000
    const poll = async () => {
      if (Date.now() - triggeredAt * 1000 > maxPollTime) {
        delete this._pdfPollingTimers[sequence]
        this._restoreExportButton(sequence, null)
        return
      }
      const data = await this._ajax("POST", "check_if_final_pdf_is_generating", { type: docType, sequence: sequence, triggered_at: triggeredAt })
      if (data && !data.generating) {
        delete this._pdfPollingTimers[sequence]
        this._restoreExportButton(sequence, data.pdf_url)
      } else {
        this._pdfPollingTimers[sequence] = setTimeout(poll, 3000)
      }
    }
    this._pdfPollingTimers[sequence] = setTimeout(poll, 3000)
  }

  _setExportGenerating(sequence) {
    const card = this.element.querySelector(`.pco-card[data-sequence="${sequence}"]`)
    if (!card) return
    const area = card.querySelector("[data-export-area]")
    if (!area) return
    area.innerHTML = `<span class="inline-flex items-center gap-1.5 text-amber-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px]"><svg class="w-3 h-3 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Generating PDF...</span>`
  }

  _restoreExportButton(sequence, pdfUrl) {
    const card = this.element.querySelector(`.pco-card[data-sequence="${sequence}"]`)
    if (!card) return
    const area = card.querySelector("[data-export-area]")
    if (!area) return
    const url = pdfUrl || `/projects/${this.projectIdValue}/finances/pco_export_with_pdfs/${sequence}.pdf`
    area.innerHTML = `<div class="relative" data-controller="dropdown">
      <button data-action="click->dropdown#toggle" class="inline-flex items-center gap-1 bg-gray-200 hover:bg-gray-300 text-gray-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px] transition-colors cursor-pointer">Export <svg class="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 10 6"><path d="M0 0l5 6 5-6z"/></svg></button>
      <div data-dropdown-target="menu" class="hidden absolute right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-30 min-w-[100px] py-1">
        <a href="${url}" target="_blank" data-action="click->proposed-change-order#stopPropagation" class="block px-3 py-1 text-[13px] text-gray-700 hover:bg-gray-50">PDF</a>
      </div>
    </div>`
  }

  // -- Toast --

  _showToast(message, type = "success", dismissAfter = 5000) {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.style.zIndex = "9999"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", String(dismissAfter))
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  // ── CCD Methods ──────────────────────────────────────────────────

  async newCcd() {
    if (!this.sovApprovedValue) {
      this._showToast("Schedule of values must be approved before creating a CCD.", "alert")
      return
    }
    this._mode = "ccd"
    this._editSequence = null
    this._isPast = false
    this._linkedCo = null
    this._isVoid = false
    this._ccdData = null
    if (this.hasModalTitleTarget) this.modalTitleTarget.textContent = "New CCD"
    await this._loadLineItemsAndShowModal()
  }

  async editCcd(event) {
    const sequence = event.currentTarget.dataset.sequence
    this._mode = "ccd"
    this._editSequence = sequence
    this._isPast = false
    const card = this.element.querySelector(`.ccd-card[data-sequence="${sequence}"]`)
    this._isVoid = card?.dataset.isVoid === "true"
    if (this.hasModalTitleTarget) this.modalTitleTarget.textContent = this._isVoid ? `CCD #${sequence}` : `Edit CCD #${sequence}`
    await this._loadLineItemsAndShowModal(sequence)
  }

  async updateCcd() { await this._submitCcd() }

  async _submitCcd(silent = false) {
    if (this._submitting) return

    if (!this._skipValidation && !this._validatePco(false)) return

    const collected = this._collectPcoLineItems()
    if (collected.items.length === 0 && !collected.fee) {
      this._showToast("Please enter at least one line item with an Amount or Days value.", "alert")
      return
    }

    this._submitting = true
    if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.disabled = true

    try {
      const sequence = this._editSequence || this._getNextCcdSequence()
      const isUpdate = !!this._editSequence
      const action = isUpdate ? "update_ccd_line_items" : "create_ccd_line_items"

      const params = {
        sequence: sequence,
        line_items_arr: JSON.stringify(collected.items),
        fee_amount: collected.fee ? collected.fee.amount : "",
        fee_days: collected.fee ? collected.fee.days : "",
        fee_comment: collected.fee ? collected.fee.comment : "",
        title: this.pcoTitleTarget.value,
        attachment_ids: this._attachmentIds.join(" ")
      }

      const method = isUpdate ? "PUT" : "POST"
      const saveResp = await this._ajax(method, action, params)

      if (saveResp) {
        this._editSequence = sequence
        if (isUpdate) {
          this._updateCcdPageCard(saveResp)
        } else {
          this._insertNewCcdPageCard(saveResp)
        }
        this._updateOverallTotals(saveResp.overall_total, saveResp.overall_days)
      }

      if (!silent) {
        this.hideNewPcoModal()
        this._showToast("Saved", "success")
      }
      this._startPdfPolling(sequence, "CCD")
    } finally {
      this._submitting = false
      if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.disabled = false
    }
  }

  async showIssueCcdModal() {
    if (this._attachmentIds.length === 0) {
      this._showToast("A PDF attachment is required before issuing a CCD.", "alert")
      return
    }

    // For new (unsaved) CCDs, save as draft first then show issue confirmation
    if (!this._editSequence) {
      await this._submitCcd(true)
      if (!this._editSequence) return // validation failed or save errored
    }
    // Populate notification user list
    if (this.hasIssueCcdNotifyListTarget) {
      let html = ""
      this._ccdNotifyUsers.forEach(u => {
        const displayName = u.name?.trim() ? this._escAttr(u.name) : this._escAttr(u.email)
        html += `<label class="flex items-center gap-2 text-[13px] text-gray-700">
          <input type="checkbox" value="${this._escAttr(u.email)}" ${u.preselected ? "checked" : ""} class="rounded border-gray-300 text-brand focus:ring-brand">
          <span>${displayName}</span>
          <span class="text-gray-400 text-[12px]">(${this._escAttr(u.company)})</span>
        </label>`
      })
      if (!html) html = '<p class="text-[13px] text-gray-400 italic">No users available for notification</p>'
      this.issueCcdNotifyListTarget.innerHTML = html
    }
    this.issueCcdModalTarget.classList.remove("hidden")
  }

  hideIssueCcdModal() {
    this.issueCcdModalTarget.classList.add("hidden")
  }

  async confirmIssueCcd() {
    const emails = []
    if (this.hasIssueCcdNotifyListTarget) {
      this.issueCcdNotifyListTarget.querySelectorAll("input[type='checkbox']:checked").forEach(cb => {
        emails.push(cb.value)
      })
    }

    const resp = await this._ajax("POST", "issue_ccd", {
      sequence: this._editSequence,
      notify_emails: emails.join(",")
    })

    if (resp?.error) {
      this._showToast(resp.error, "alert")
      return
    }

    if (resp) {
      this._updateCcdPageCard(resp)
      this._updateOverallTotals(resp.overall_total, resp.overall_days)
    }

    this.hideIssueCcdModal()
    this.hideNewPcoModal()
    this._showToast("CCD Issued", "success")
    this._startPdfPolling(this._editSequence, "CCD")
  }

  showDeleteCcdModal() {
    if (this.hasDeleteCcdModalTarget) {
      this.deleteCcdModalTarget.classList.remove("hidden")
    }
  }

  hideDeleteCcdModal() {
    if (this.hasDeleteCcdModalTarget) this.deleteCcdModalTarget.classList.add("hidden")
  }

  async confirmDeleteCcd() {
    const resp = await this._ajax("DELETE", "destroy_ccd_line_items", { sequence: this._editSequence })
    if (resp?.error) {
      this._showToast(resp.error, "alert")
      this.hideDeleteCcdModal()
      return
    }

    const card = this.element.querySelector(`.ccd-card[data-sequence="${this._editSequence}"]`)
    if (card) card.remove()

    if (resp) this._updateOverallTotals(resp.overall_total, resp.overall_days)

    this.hideDeleteCcdModal()
    this.hideNewPcoModal()
    this._showToast("CCD deleted", "success")

    // Update empty state
    const hasCards = this.element.querySelectorAll(".pco-card, .ccd-card").length > 0
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.toggle("hidden", hasCards)
    if (this.hasOverallTotalsTarget) this.overallTotalsTarget.classList.toggle("hidden", !hasCards)
  }

  async voidCcd() {
    const seq = this._editSequence
    const resp = await this._ajax("POST", "void_ccd_line_items", { sequence: seq })
    if (resp) {
      this._updateCcdPageCard(resp)
      this._updateOverallTotals(resp.overall_total, resp.overall_days)
    }
    this.hideNewPcoModal()
    this._showToast("CCD voided", "success")
    this._startPdfPolling(seq, "CCD")
  }

  async unvoidCcd() {
    const seq = this._editSequence
    const resp = await this._ajax("POST", "unvoid_ccd_line_items", { sequence: seq })
    if (resp) {
      this._updateCcdPageCard(resp)
      this._updateOverallTotals(resp.overall_total, resp.overall_days)
    }
    this.hideNewPcoModal()
    this._showToast("CCD unvoided", "success")
    this._startPdfPolling(seq, "CCD")
  }

  _getNextCcdSequence() {
    const ccdElements = this.element.querySelectorAll(".ccd-card[data-sequence]")
    let max = 0
    ccdElements.forEach(el => {
      const seq = parseInt(el.dataset.sequence)
      if (seq > max) max = seq
    })
    return max + 1
  }

  _renderCcdModalHeaderBadges(data) {
    this._resetModalHeaderBadges()

    // Show CCD status in modal title area
    const ccdData = data.ccd_data || this._ccdData
    if (data.linked_co) {
      if (this.hasModalTitleTarget) this.modalTitleTarget.textContent = `CCD #${this._editSequence}`
    } else if (ccdData && ccdData.status === "issued") {
      if (this.hasModalTitleTarget) this.modalTitleTarget.textContent = `CCD #${this._editSequence}`
      // Show Issue CCD button as disabled since already issued
      if (this.hasIssueCcdBtnTarget) {
        this.issueCcdBtnTarget.classList.add("hidden")
      }
    }

    // Linked CO
    if (this.hasModalLinkedCoTarget) {
      if (data.linked_co) {
        this.modalLinkedCoTarget.classList.remove("hidden")
        if (this.hasModalLinkedCoTextTarget) this.modalLinkedCoTextTarget.textContent = `Added to CO#${data.linked_co}`
      } else {
        this.modalLinkedCoTarget.classList.add("hidden")
      }
    }

    // CCD footer buttons
    const isIssued = ccdData?.status === "issued"
    const isEditable = ccdData?.editable !== false
    const isVoid = data.ccd_data?.is_void || this._isVoid
    const canManageCcd = this.ccdEnabledValue
    const linkedCo = this._linkedCo

    if (this.hasModalDeleteBtnTarget) this.modalDeleteBtnTarget.classList.toggle("hidden", !this._editSequence || !isEditable || !canManageCcd || !!linkedCo)
    if (this.hasModalVoidBtnTarget) this.modalVoidBtnTarget.classList.toggle("hidden", !this._editSequence || isVoid || !canManageCcd || !!linkedCo)
    if (this.hasModalUnvoidBtnTarget) this.modalUnvoidBtnTarget.classList.toggle("hidden", !this._editSequence || !isVoid || !canManageCcd || !!linkedCo)

    if (linkedCo) {
      // Linked to CO: hide all action buttons, modal is readonly
      if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.classList.add("hidden")
      if (this.hasIssueCcdBtnTarget) this.issueCcdBtnTarget.classList.add("hidden")
      if (this.hasUpdateCcdBtnTarget) this.updateCcdBtnTarget.classList.add("hidden")
    } else if (isIssued && !isVoid) {
      // Issued CCD: hide Save Draft and Issue, show Update CCD only for owners/consultants with permission
      if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.classList.add("hidden")
      if (this.hasIssueCcdBtnTarget) this.issueCcdBtnTarget.classList.add("hidden")
      if (this.hasUpdateCcdBtnTarget) this.updateCcdBtnTarget.classList.toggle("hidden", !canManageCcd)
    } else {
      // Draft/new CCD: show Save Draft and Issue, hide Update
      if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.classList.toggle("hidden", isVoid && !!this._editSequence)
      if (this.hasIssueCcdBtnTarget) this.issueCcdBtnTarget.classList.toggle("hidden", isVoid || !isEditable)
      if (this.hasUpdateCcdBtnTarget) this.updateCcdBtnTarget.classList.add("hidden")
    }
  }

  _buildCcdCardHTML(data) {
    const seq = data.sequence
    const isVoid = data.is_void
    const status = data.status || "draft"
    const total = parseFloat(data.ccd_total) || 0
    const totalFormatted = this._formatCurrency(total)

    let badgeHTML = ""
    if (isVoid) {
      badgeHTML = '<span class="inline-flex items-center gap-1 bg-red-100 text-red-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px]">VOID</span>'
    } else if (status === "issued") {
      let tooltip = ""
      if (data.issued_by_name && data.issued_at) {
        tooltip = ` <span class="shrink-0 cursor-help flex items-center" data-tip="Issued by ${this._escAttr(data.issued_by_name)} on ${this._escAttr(data.issued_at)}" data-tip-align="left" data-tip-pos="below">${SVG_INFO_CIRCLE}</span>`
      }
      badgeHTML = `<span class="inline-flex items-center gap-1 bg-green-100 text-green-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px]">${SVG_CHECK} Issued${tooltip}</span>`
    } else {
      badgeHTML = '<span class="inline-flex items-center gap-1 bg-gray-200 text-gray-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px]">DRAFT</span>'
    }

    let linkedCoHTML = ""
    if (data.linked_co) {
      linkedCoHTML = `<span class="inline-flex items-center gap-1 bg-blue-50 text-blue-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px]">${SVG_LINK} Added to CO#${data.linked_co}</span>`
    }

    const titleHTML = data.title ? `<div data-pco-title class="px-3 py-1 text-[13px] text-gray-600 border-t border-gray-200"><span class="font-medium">Title:</span> ${this._escAttr(data.title)}</div>` : ""

    let rowsHTML = ""
    ;(data.line_items || []).forEach(li => {
      if (li.line_item_id) {
        const amt = parseFloat(li.amount) || 0
        const amtStr = amt < 0 ? `<span class="text-red-600">${this._formatCurrency(amt)}</span>` : this._formatCurrency(amt)
        const days = parseInt(li.days) || 0
        const daysStr = days < 0 ? `<span class="text-red-600">(${Math.abs(days)})</span>` : `${days}`
        rowsHTML += `<tr class="bg-white border-b border-l border-r border-gray-200">
          <td class="px-2 py-1 border-r border-gray-200">${li.item || ""}</td>
          <td class="px-2 py-1 border-r border-gray-200">${li.csi_div || ""}</td>
          <td class="px-2 py-1">${li.description || ""}</td><td class="px-1 py-1"></td>
          <td class="px-2 py-1 border-l border-gray-200">${this._escAttr(li.comment || "")}</td>
          <td class="px-2 py-1 text-right border-l border-r border-gray-200 ${amt < 0 ? "text-red-600" : ""}">${amtStr}</td>
          <td class="px-2 py-1 text-right">${daysStr}</td></tr>`
      } else if (li.is_fee && (parseFloat(li.amount) !== 0 || parseInt(li.days) !== 0)) {
        rowsHTML += `<tr class="bg-white border-b border-l border-r border-gray-200">
          <td class="px-2 py-1 border-r border-gray-200"></td><td class="px-2 py-1 border-r border-gray-200"></td>
          <td class="px-2 py-1">Contractor's Fee</td><td class="px-1 py-1"></td>
          <td class="px-2 py-1 border-l border-gray-200">${this._escAttr(li.comment || "")}</td>
          <td class="px-2 py-1 text-right border-l border-r border-gray-200">${this._formatCurrency(parseFloat(li.amount) || 0)}</td>
          <td class="px-2 py-1 text-right">${parseInt(li.days) || 0}</td></tr>`
      }
    })

    const days = parseInt(data.ccd_days) || 0
    const daysTotal = days < 0 ? `<span class="text-red-600">(${Math.abs(days)})</span>` : `${days}`

    const pdfUrl = data.final_pdf_url || `/projects/${this.projectIdValue}/finances/ccd_export_with_pdfs/${seq}.pdf`

    return `<div class="ccd-card bg-white border border-gray-200 rounded-lg" data-type="ccd" data-sequence="${seq}" data-is-void="${isVoid}">
      <div class="flex items-center justify-between px-3 py-2.5 text-[13px] text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer" data-action="click->proposed-change-order#editCcd" data-sequence="${seq}">
        <div class="flex items-center gap-3">
          <span class="font-semibold text-gray-700 text-[14px]">CCD #${seq}</span>
          <span data-badge-area>${badgeHTML}</span>
          ${linkedCoHTML}
        </div>
        <div class="flex items-center gap-3">
          <span data-export-area>
            <div class="relative" data-controller="dropdown">
              <button data-action="click->dropdown#toggle" class="inline-flex items-center gap-1 bg-gray-200 hover:bg-gray-300 text-gray-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px] transition-colors cursor-pointer">Export <svg class="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 10 6"><path d="M0 0l5 6 5-6z"/></svg></button>
              <div data-dropdown-target="menu" class="hidden absolute right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-30 min-w-[100px] py-1">
                <a href="${pdfUrl}" target="_blank" data-action="click->proposed-change-order#stopPropagation" class="block px-3 py-1 text-[13px] text-gray-700 hover:bg-gray-50">PDF</a>
              </div>
            </div>
          </span>
          <span data-last-updated class="text-gray-400 text-[12px] italic">Last updated on ${data.last_updated_at || ""} by ${data.who_updated || ""}</span>
          <span data-card-total-amount class="font-semibold text-gray-700 text-[14px]">${totalFormatted}</span>
        </div>
      </div>
      ${titleHTML}
      <table data-pco-table class="w-full text-xs text-gray-600 border-collapse" style="table-layout:fixed">
        <colgroup><col style="width:50px"><col style="width:65px"><col style="width:320px"><col style="width:24px"><col><col style="width:150px"><col style="width:80px"></colgroup>
        <thead><tr class="bg-white border border-gray-200">
          <th class="px-2 py-1 font-medium text-gray-500 border-r border-gray-200 text-left">Item</th>
          <th class="px-2 py-1 font-medium text-gray-500 border-r border-gray-200 text-left">CSI Div</th>
          <th class="px-2 py-1 font-medium text-gray-500 text-left">Work</th><th></th>
          <th class="px-2 py-1 font-medium text-gray-500 border-l border-gray-200 text-left">Comments</th>
          <th class="px-2 py-1 font-medium text-gray-500 border-l border-r border-gray-200 text-right">Amount</th>
          <th class="px-2 py-1 font-medium text-gray-500 text-right">Days</th>
        </tr></thead>
        <tbody>${rowsHTML}
          <tr class="bg-white border-b border-l border-r border-gray-200 font-semibold">
            <td colspan="5" class="px-2 py-1 text-right border-r border-gray-200">Total</td>
            <td class="px-2 py-1 text-right border-l border-r border-gray-200 ${total < 0 ? "text-red-600" : ""}">${totalFormatted}</td>
            <td class="px-2 py-1 text-right">${daysTotal}</td>
          </tr>
        </tbody>
      </table>
    </div>`
  }

  _insertNewCcdPageCard(data) {
    if (this.hasPcoCardsTarget) {
      this.pcoCardsTarget.classList.add("flex", "flex-col", "gap-5")
      // New CCD is the most recent item — insert at the top
      this.pcoCardsTarget.insertAdjacentHTML("afterbegin", this._buildCcdCardHTML(data))
    }
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
    if (this.hasOverallTotalsTarget) this.overallTotalsTarget.classList.remove("hidden")
  }

  _updateCcdPageCard(data) {
    const card = this.element.querySelector(`.ccd-card[data-sequence="${data.sequence}"]`)
    if (card) {
      card.outerHTML = this._buildCcdCardHTML(data)
    }
  }

  // -- AJAX helper --

  async _ajax(method, action, params = {}) {
    const url = `/projects/${this.projectIdValue}/finances/${action}`
    const body = new FormData()
    Object.entries(params).forEach(([k, v]) => body.append(k, v))

    const options = {
      method: method === "GET" ? "GET" : "POST",
      headers: { "X-CSRF-Token": this._csrfToken, "Accept": "application/json" },
    }

    if (method !== "GET") {
      if (method !== "POST") body.append("_method", method.toLowerCase())
      options.body = body
    }

    try {
      const resp = await fetch(url, options)
      if (resp.headers.get("content-type")?.includes("application/json")) {
        return await resp.json()
      }
      return resp.ok
    } catch (e) {
      console.error("PCO request failed:", e)
    }
  }
}
